import { useMemo } from 'react';

import { Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { FormikProvider, getIn, useFormik } from 'formik';
import * as Yup from 'yup';

import { ClusterAutoComplete, CondicaoPagamentoAutoComplete, TipoLojaAutoComplete } from 'components/autocompletes';
import { CissAutoComplete } from 'components/autocompletes/CissAutoComplete';
import { BbarForm } from 'components/docked/BbarForm';
import { CissMaskField, CissNumberField, CissTextField, Editor } from 'components/fields';
import { Row } from 'components/form';
import { Block } from 'components/page';
import { SubTitle } from 'components/text';
import { useLoaderContext } from 'context/LoaderContext';
import { usePermissionsContext } from 'context/PermissionsContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import { LojaBandaPrecoGrid, LojaConfiguracaoPropriedadeGrid, LojaFormDirtyListener } from 'pages/cadastro/loja/components';
import { useLojaDirty, useMutateDadosBasicos, useQueryDadosBasicos } from 'pages/cadastro/loja/hooks';
import { initialValuesFormik } from 'pages/cadastro/loja/types';
import { CepField } from 'pages/cadastro/usuario/components';

export function LojaDadosBasicosForm() {
    const { hasPermission } = usePermissionsContext();
    const { onSubmit, isLoading } = useMutateDadosBasicos();
    const { data, isFetching } = useQueryDadosBasicos();
    const { focusError } = useFocusErrorFormik();

    useLoaderContext(isFetching || isLoading);

    const validationSchema = useMemo(() => {
        return Yup.object({
            cdLoja: Yup.string().required(),
            dsRazaoSocial: Yup.string().required(),
            dsNomeFantasia: Yup.string().required(),
            dsCnpj: Yup.string().required().length(14),
            dsEmail: Yup.string().required(),
            dsEndereco: Yup.string().required(),
            nrEndereco: Yup.number().required(),
            dsBairro: Yup.string().required(),
            dsComplemento: Yup.string().nullable(),
            fgAtivo: Yup.string().required(),
            tipoLoja: Yup.object().required(),
            lojaPerfil: Yup.object().required(),
            grupoEconomicoLoja: Yup.object().required(),
            lojaCondicaoPagamento: Yup.object().required(),
            dtInauguracao: Yup.string().required(),
            cdPontoComercial: Yup.string().max(100),
            cep: Yup.object()
                .shape({
                    nrCep: Yup.string().length(8, 'O campo deve ter exatamente 8 números.').required(),
                    estado: Yup.object().required(),
                    cidade: Yup.object().required(),
                })
                .required(),
            lojaConfiguracao: Yup.array()
                .of(
                    Yup.object().shape({
                        idLojaConfiguracao: Yup.number(),
                        configuracaoPropriedade: Yup.object().shape({
                            idConfiguracaoPropriedade: Yup.number(),
                            dsConfiguracao: Yup.string(),
                        }),
                        idConfiguracaoPropriedade: Yup.number(),
                        dsConfiguracao: Yup.string(),
                        dsChave: Yup.string(),
                        dsValor: Yup.string(),
                    }),
                )
                .nullable(),
            bandaPreco: Yup.array()
                .of(
                    Yup.object().shape({
                        bandaPreco: Yup.object().shape({
                            idBandaPreco: Yup.number(),
                            dsBandaPreco: Yup.string(),
                        }),
                        idBandaPreco: Yup.string(),
                        dsBandaPreco: Yup.string(),
                        dsValor: Yup.string(),
                        vlPercentual: Yup.string(),
                        fgAtivo: Yup.boolean(),
                    }),
                )
                .nullable(),
        });
    }, []);

    const formik = useFormik({
        initialValues: data || initialValuesFormik,
        validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit,
        enableReinitialize: true,
    });

    const { errors, values, setFieldValue, dirty, submitForm } = formik;

    focusError(errors);

    useLojaDirty(dirty);

    /**
     * Retorna o erro do campo de cep, caso o cep esteja inválido, retorna o erro do campo especificado.
     */
    const getErrorInCep = (field: string): string => (typeof getIn(errors, 'cep') === 'string' ? getIn(errors, 'cep') : getIn(errors, field));

    return (
        <FormikProvider value={formik}>
            <LojaFormDirtyListener />

            <Block>
                <SubTitle label="Dados Básicos da Loja" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Código"
                            name="cdLoja"
                            value={values.cdLoja}
                            error={errors.cdLoja}
                            onChange={(e) => setFieldValue('cdLoja', e.target.value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 2 }}>
                        <CissTextField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Razão Social"
                            name="dsRazaoSocial"
                            value={values.dsRazaoSocial}
                            error={errors.dsRazaoSocial}
                            onChange={(e) => setFieldValue('dsRazaoSocial', e.target.value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 2 }}>
                        <CissTextField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Nome Fantasia"
                            name="dsNomeFantasia"
                            value={values.dsNomeFantasia}
                            error={errors.dsNomeFantasia}
                            onChange={(e) => setFieldValue('dsNomeFantasia', e.target.value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                            <DatePicker
                                sx={{ width: '100%' }}
                                disabled={!hasPermission(['LOJA_ALTERAR'])}
                                label="Data de Inauguração"
                                format="dd/MM/yyyy"
                                name="dtInauguracao"
                                value={values.dtInauguracao ? new Date(values.dtInauguracao) : null}
                                onChange={(value) => setFieldValue('dtInauguracao', value)}
                                slotProps={{
                                    textField: {
                                        error: Boolean(errors.dtInauguracao),
                                        helperText: errors.dtInauguracao,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissMaskField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="CNPJ"
                            name="dsCnpj"
                            format="cnpj"
                            value={values.dsCnpj}
                            error={errors.dsCnpj}
                            onChange={(e) => setFieldValue('dsCnpj', e.target.value)}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Inscrição Estadual"
                            name="dsInscricaoEstadual"
                            value={values.dsInscricaoEstadual}
                            error={errors.dsInscricaoEstadual}
                            onChange={(e) => setFieldValue('dsInscricaoEstadual', e.target.value)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissMaskField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Telefone da Loja"
                            format="phone"
                            name="dsFone"
                            value={values.dsFone}
                            onChange={(e) => setFieldValue('dsFone', e.target.value)}
                            error={errors.dsFone}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Email da Loja"
                            name="dsEmail"
                            value={values.dsEmail}
                            onChange={(e) => setFieldValue('dsEmail', e.target.value)}
                            error={errors.dsEmail}
                        />
                    </Grid>
                </Row>
            </Block>

            <Block>
                <SubTitle label="Observação" />

                <Editor
                    value={values.dsObservacao}
                    onChange={(value) => setFieldValue('dsObservacao', value)}
                    disabled={!hasPermission(['LOJA_ALTERAR'])}
                />
            </Block>

            <Block>
                <SubTitle label="Endereço da Loja" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CepField value={values.cep?.nrCep || ''} error={getErrorInCep('cep.nrCep')} disabled={!hasPermission(['LOJA_ALTERAR'])} />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Endereço"
                            name="dsEndereco"
                            value={values.dsEndereco || ''}
                            error={errors.dsEndereco}
                            onChange={(e) => setFieldValue('dsEndereco', e.target.value)}
                            disabled={!hasPermission(['LOJA_ALTERAR']) || !values.cep?.cidade?.dsCidade}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissNumberField
                            label="Número"
                            name="nrEndereco"
                            preventEmptyField={false}
                            value={values.nrEndereco || ''}
                            error={errors.nrEndereco}
                            onChange={(e) => setFieldValue('nrEndereco', e.target.value)}
                            disabled={!hasPermission(['LOJA_ALTERAR']) || !values.cep?.cidade?.dsCidade}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Complemento"
                            name="dsComplemento"
                            value={values.dsComplemento || ''}
                            error={errors.dsComplemento}
                            onChange={(e) => setFieldValue('dsComplemento', e.target.value)}
                            disabled={!hasPermission(['LOJA_ALTERAR']) || !values.cep?.cidade?.dsCidade}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Bairro"
                            name="dsBairro"
                            value={values.dsBairro || ''}
                            error={errors.dsBairro}
                            onChange={(e) => setFieldValue('dsBairro', e.target.value)}
                            disabled={!hasPermission(['LOJA_ALTERAR']) || !values.cep?.cidade?.dsCidade}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label={'Cidade'}
                            name="dsCidade"
                            value={values.cep?.cidade?.dsCidade || ''}
                            error={getErrorInCep('cep.cidade')}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label={'Estado'}
                            name="dsEstado"
                            value={values.cep?.estado?.dsEstado || ''}
                            error={getErrorInCep('cep.estado')}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>
                </Row>
            </Block>

            <Block>
                <SubTitle label="Configurações da Loja" />

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CissAutoComplete
                            label={'Grupo Econômico'}
                            name="grupoEconomicoLoja"
                            value={values.grupoEconomicoLoja || null}
                            onChange={(e, value) => setFieldValue('grupoEconomicoLoja', value)}
                            multiple={false}
                            requestOptions={{
                                url: '/gestao/grupoeconomico',
                                columns: 'idGrupoEconomico,dsGrupoEconomico,cdGrupoEconomico,fgAtivo',
                                sort: [{ property: 'idGrupoEconomico', direction: 'ASC' }],
                            }}
                            isOptionEqualToValue={(option, value) => option.idGrupoEconomico === value.idGrupoEconomico}
                            optionStringTemplate="<strong>{idGrupoEconomico}</strong> - {dsGrupoEconomico}"
                            selectedStringTemplate="{idGrupoEconomico} - {dsGrupoEconomico}"
                            error={errors.grupoEconomicoLoja}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissAutoComplete
                            label={'Faixa Faturamento'}
                            name="faixaFaturamento"
                            value={values.faixaFaturamento || null}
                            onChange={(e, value) => setFieldValue('faixaFaturamento', value)}
                            multiple={false}
                            requestOptions={{
                                url: '/gestao/faixafaturamento',
                                columns: 'idFaixaFaturamento,dsFaixaFaturamento',
                                sort: [{ property: 'idFaixaFaturamento', direction: 'ASC' }],
                            }}
                            isOptionEqualToValue={(option, value) => option.idFaixaFaturamento === value.idFaixaFaturamento}
                            optionStringTemplate="<strong>{idFaixaFaturamento}</strong> - {dsFaixaFaturamento}"
                            selectedStringTemplate="{idFaixaFaturamento} - {dsFaixaFaturamento}"
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <TipoLojaAutoComplete
                            name="tipoLoja"
                            value={values.tipoLoja || null}
                            onChange={(e, value) => setFieldValue('tipoLoja', value)}
                            error={errors.tipoLoja}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissAutoComplete
                            label={'Perfil da loja'}
                            name="lojaPerfil"
                            value={values.lojaPerfil || null}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            onChange={(e, value) => setFieldValue('lojaPerfil', value)}
                            multiple={false}
                            requestOptions={{
                                url: '/gestao/lojaperfil',
                                columns: 'idLojaPerfil,dsLojaPerfil',
                                sort: [{ property: 'idLojaPerfil', direction: 'ASC' }],
                            }}
                            isOptionEqualToValue={(option, value) => option.idLojaPerfil === value.idLojaPerfil}
                            optionStringTemplate="<strong>{idLojaPerfil}</strong> - {dsLojaPerfil}"
                            selectedStringTemplate="{idLojaPerfil} - {dsLojaPerfil}"
                            error={errors.lojaPerfil}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <CondicaoPagamentoAutoComplete
                            name="lojaCondicaoPagamento"
                            value={values.lojaCondicaoPagamento || null}
                            onChange={(e, value) => setFieldValue('lojaCondicaoPagamento', value)}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            error={errors.lojaCondicaoPagamento}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            name="cdPontoComercial"
                            value={values.cdPontoComercial}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                            label="Código Ponto Comercial"
                            onChange={(e) => setFieldValue('cdPontoComercial', e.target.value)}
                            error={errors.cdPontoComercial}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ width: { sx: '100%', lg: '50%' }, pr: { xs: 0, lg: 1 } }}>
                        <ClusterAutoComplete
                            name="clusterLoja"
                            size="medium"
                            value={values.clusterLoja || null}
                            error={errors.clusterLoja}
                            onChange={(e, value) => setFieldValue('clusterLoja', value)}
                        />
                    </Grid>
                </Row>

                <Row>
                    <Grid sx={{ flex: 1 }}>
                        <LojaConfiguracaoPropriedadeGrid
                            lojaConfiguracoes={values.lojaConfiguracoes}
                            setFieldValue={setFieldValue}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>

                    <Grid sx={{ flex: 1 }}>
                        <LojaBandaPrecoGrid
                            lojaBandaPrecos={values.lojaBandaPrecos}
                            setFieldValue={setFieldValue}
                            disabled={!hasPermission(['LOJA_ALTERAR'])}
                        />
                    </Grid>
                </Row>
            </Block>

            <BbarForm submitForm={submitForm} fgAtivo />
        </FormikProvider>
    );
}
